import { useEffect } from "react";
import { getToken, messaging, FCM_TOKEN_KEY } from "../firebase";

async function sendTokenToServer(fcmToken, profile, authToken) {
  try {
    const res = await fetch(
      `/api/v1/users/fcm?patientId=${profile.patientId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ fcmToken, platform: "web" }),
      }
    );
    if (!res.ok) {
      const text = await res.text();
      console.error(
        "Failed to register FCM token on server:",
        res.status,
        text
      );
      return false;
    }
    console.log("FCM token registered on server");
    return true;
  } catch (err) {
    console.error("Network error while registering FCM token:", err);
    return false;
  }
}

async function waitForServiceWorkerReady() {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers not supported");
  }

  // Check if we already have a registration
  const registration = await navigator.serviceWorker.getRegistration();

  if (registration) {
    console.log("FCM: Found existing registration");

    // If it has an active worker, we're good
    if (registration.active) {
      console.log("FCM: Active service worker found");
      return registration;
    }

    // Otherwise wait for it to become active
    if (registration.installing || registration.waiting) {
      console.log("FCM: Waiting for service worker to activate...");
      return new Promise((resolve) => {
        const checkState = () => {
          if (registration.active) {
            resolve(registration);
          } else {
            setTimeout(checkState, 100);
          }
        };
        checkState();
      });
    }
  }

  // Fall back to navigator.serviceWorker.ready
  console.log("FCM: Waiting for service worker ready...");
  return navigator.serviceWorker.ready;
}

function FCMProvider({ profile, authToken }) {
  useEffect(() => {
    if (!profile || !authToken) return;
    let cancelled = false;

    const init = async () => {
      console.log("FCM: Starting initialization...");

      if (!("Notification" in window)) return;

      if (Notification.permission === "denied") return;
      if (Notification.permission === "default") {
        const p = await Notification.requestPermission();
        if (p !== "granted") return;
      }

      try {
        const swReg = await waitForServiceWorkerReady();

        if (cancelled) return;

        console.log("FCM: Service worker ready, getting token...");

        const fcmToken = await getToken(messaging, {
          vapidKey: profile.vapidPublicKey,
          serviceWorkerRegistration: swReg,
        });

        console.log(
          "FCM: Token result:",
          fcmToken ? "✓ Got token" : "✗ No token"
        );

        if (!fcmToken || cancelled) return;

        const cached = localStorage.getItem(FCM_TOKEN_KEY);
        if (cached === fcmToken) {
          console.log("FCM: Token already cached");
          return;
        }

        const ok = await sendTokenToServer(fcmToken, profile, authToken);
        if (ok) localStorage.setItem(FCM_TOKEN_KEY, fcmToken);
      } catch (error) {
        console.error("FCM: Error during initialization:", error);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [profile, authToken]);

  return null;
}

export default FCMProvider;
